import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { radii } from '../../theme';

/**
 * Shared Recharts styling so every chart uses the same grid, axis and tick
 * treatment. Spread the pieces onto the matching Recharts components:
 *
 *   const cs = useChartStyles();
 *   <CartesianGrid {...cs.grid} />
 *   <XAxis {...cs.xAxis} dataKey="date" />
 */
export const useChartStyles = () => {
  const theme = useTheme();
  const muted = theme.palette.text.secondary;
  const line = theme.palette.divider;
  const tick = { fontSize: 12, fill: muted };

  return {
    muted,
    line,
    tick,
    grid: { stroke: line, strokeDasharray: '3 4', vertical: false },
    gridBoth: { stroke: line, strokeDasharray: '3 4' },
    xAxis: { tick, axisLine: { stroke: line }, tickLine: false as const, tickMargin: 8 },
    yAxis: { tick, axisLine: false as const, tickLine: false as const, tickMargin: 6 },
    /** Category axis on a horizontal bar chart: readable, unrotated labels. */
    categoryAxis: {
      tick: { fontSize: 12.5, fill: theme.palette.text.primary },
      axisLine: false as const,
      tickLine: false as const,
    },
    axisLabel: (value: string, angle = -90) => ({
      value,
      angle,
      position: (angle === 0 ? 'insideBottom' : 'insideLeft') as 'insideBottom' | 'insideLeft',
      offset: angle === 0 ? 0 : 4,
      style: { textAnchor: 'middle' as const, fontSize: 12, fill: muted },
    }),
    /** Light band behind each bar in a ranking chart. */
    barBackground: { fill: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.035) },
    cursor: { fill: alpha(theme.palette.primary.main, 0.06) },
    lineCursor: { stroke: alpha(theme.palette.text.primary, 0.25), strokeWidth: 1 },
  };
};

export interface ChartTooltipRow {
  key?: string;
  color?: string;
  label: React.ReactNode;
  value: React.ReactNode;
}

/** The floating card used by custom Recharts tooltips. */
export const ChartTooltipCard: React.FC<{ title?: React.ReactNode; rows: ChartTooltipRow[]; footer?: React.ReactNode }> = ({
  title,
  rows,
  footer,
}) => (
  <Box
    sx={(theme) => ({
      bgcolor: 'background.paper',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: `${radii.nested}px`,
      boxShadow: `0 10px 28px ${alpha('#1B1526', theme.palette.mode === 'dark' ? 0.5 : 0.14)}`,
      px: 1.5,
      py: 1.25,
      minWidth: 160,
      maxWidth: 320,
    })}
  >
    {title && (
      <Typography variant="body2" sx={{ fontWeight: 600, mb: rows.length ? 0.75 : 0 }}>
        {title}
      </Typography>
    )}
    {rows.map((row, i) => (
      <Box
        key={row.key ?? i}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.2, fontSize: '0.8125rem' }}
      >
        {row.color && (
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: row.color, flexShrink: 0 }} />
        )}
        <Box component="span" sx={{ color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.label}
        </Box>
        <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', pl: 1 }}>
          {row.value}
        </Box>
      </Box>
    ))}
    {footer && (
      <Typography variant="caption" component="div" sx={{ mt: 0.75 }}>
        {footer}
      </Typography>
    )}
  </Box>
);

/** Translucent cover shown over a chart while new data loads. */
export const ChartLoadingOverlay: React.FC<{ label?: string }> = ({ label = 'Updating...' }) => (
  <Box
    sx={(theme) => ({
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: alpha(theme.palette.background.paper, 0.72),
      zIndex: 10,
      borderRadius: 1,
      color: 'text.secondary',
      fontSize: '0.875rem',
      fontWeight: 500,
    })}
  >
    {label}
  </Box>
);

/**
 * Unrotated, right-aligned category label for horizontal bar charts.
 * Long names are shortened with the full name kept as an SVG tooltip.
 */
export const CategoryTick: React.FC<any> = ({ x = 0, y = 0, payload, fill, maxLength = 24, fontSize = 12.5 }) => {
  const full = String(payload?.value ?? '');
  const text = full.length > maxLength ? `${full.slice(0, maxLength - 1)}\u2026` : full;
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{full}</title>
      <text x={-8} y={0} dy="0.35em" textAnchor="end" fill={fill} fontSize={fontSize}>
        {text}
      </text>
    </g>
  );
};

/** Dark or white text, whichever reads better on the given hex/rgb color. */
export const readableTextOn = (color: string): string => {
  let r = 0, g = 0, b = 0;
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  const rgb = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else if (rgb) {
    [r, g, b] = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  } else {
    return '#FFFFFF';
  }
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.42 ? '#1B1526' : '#FFFFFF';
};
